import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import z from 'zod';

import { Button, Card, Heading4, Input, Page, Text, Stack, u } from '@ui';

import { useUserChangePassword } from '../../hooks';

import Form from './Form';

interface ChangePasswordFormValues {
    password: string;
    confirmPassword: string;
}

const formSchema = z
    .object({
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
            .regex(/[0-9]/, 'Must contain at least one number'),
        confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    });

export const UserChangePasswordScreen = () => {
    const navigate = useNavigate();
    const { register, handleSubmit, formState } = useForm<ChangePasswordFormValues>({
        resolver: zodResolver(formSchema),
    });
    const [hasResetPassword, setHasResetPassword] = useState(false);

    const toggleHasResetPassword = () => setHasResetPassword((prev) => !prev);

    const changePassword = useUserChangePassword();

    return (
        <Page>
            <Card>
                {hasResetPassword ? (
                    <Stack gap={u(2.5)}>
                        <Heading4>Password Changed</Heading4>
                        <Text color="muted">Your password has been changed.</Text>
                        <Button type="button" variant="primary" onClick={() => navigate('/')}>
                            Back home
                        </Button>
                    </Stack>
                ) : (
                    <Form
                        onSubmit={handleSubmit((data) =>
                            changePassword.mutateAsync(data.password).then(toggleHasResetPassword),
                        )}
                        title="Change your password"
                        actionText="Change password"
                        subtitle="In order to continue, please update your password."
                        isSubmitting={changePassword.isPending}
                        error={changePassword.error?.message}
                    >
                        <Input
                            type="password"
                            label="New password"
                            {...register('password')}
                            placeholder="Enter your new password"
                            error={formState.errors.password?.message}
                        />
                        <Input
                            type="password"
                            label="Confirm password"
                            {...register('confirmPassword')}
                            placeholder="Confirm your new password"
                            error={formState.errors.confirmPassword?.message}
                        />
                    </Form>
                )}
            </Card>
        </Page>
    );
};
